import React, { useEffect, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import DeleteModal from "components/Common/DeleteModal"
import Filters from "components/Common/Filters"
import Paginations from "components/Common/Paginations"
import { filters } from "components/Common/constants"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import Switch from "components/Shared/Switch"
import TableShimmer from "components/Shared/TableShimmer"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import {
  Badge,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Table,
  UncontrolledDropdown,
} from "reactstrap"
import {
  deleteIssueType,
  getAllIssueType,
  updateIssueTypeStatus,
} from "store/actions/customerSupportAction"
import { showAlert } from "store/reducers/alertReducer"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"

const IssueLists = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Support Issues | Sign Burger")

  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const {
    loading,
    isLoading,
    isUpdating,
    totalCount,
    currentPage,
    totalPages,
    itemsPerPage,
    issueTypes,
  } = useSelector(state => state.CustomerSupport)

  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [selectedId, setSelectedId] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [selectedFilters, setSelectedFilters] = useState({
    status: parseInt(searchParams.get("status") || "2"),
    sortOrder: parseInt(searchParams.get("sortOrder") || "1"),
  })

  const { userRole, accountType } = useSelector(state => state.Login)

  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    create: null,
    edit: null,
    delete: null,
  })
  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      17,
      "customer_support_1",
      accountType
    )
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      17,
      "customer_support_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      17,
      "customer_support_3",
      accountType
    )
    const isPermissionDelete = isPermissionEnabled(
      userRole,
      17,
      "customer_support_4",
      accountType
    )

    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setUsersPermissions({
      list: isPermissionList,
      create: isPermissionCreate,
      edit: isPermissionEdit,
      delete: isPermissionDelete,
    })
  }, [userRole])

  useEffect(() => {
    fetchAllIssueTypes()
  }, [])

  const fetchAllIssueTypes = () => {
    const { status, sortOrder } = selectedFilters

    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      status: status,
      orderBy: sortOrder,
      keyword: searchTerm,
    }
    dispatch(getAllIssueType(payload))
    setIsApiCalled(true)
  }

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }

  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  const handleFetchIssues = (page = 1) => {
    const { status, sortOrder } = selectedFilters

    // Initialize searchParams object
    const searchParams = new URLSearchParams()
    searchParams.set("search", searchTerm)
    searchParams.set("status", status.toString())
    searchParams.set("sortOrder", sortOrder.toString())
    searchParams.set("page", page.toString())

    // Create payload for dispatch
    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      status: Number(status), // 0 deactive, 1 active, 2 for all
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
    }

    // Dispatch action and navigate on success
    dispatch(getAllIssueType(payload))
      .then(action => {
        const { statusCode, status } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/support-issues?${searchParams.toString()}`)
        }
      })
      .catch(error => {
        // Handle error if needed
      })
  }

  const handleSearch = () => {
    handleFetchIssues(1)
  }

  const onPageChange = page => {
    handleFetchIssues(page)
  }

  // useEffect(() => {
  //   if (isApiCalled) {
  //     if (searchTerm) {
  //       const delayDebounceFn = setTimeout(() => {
  //         handleSearch()
  //       }, 500)

  //       return () => clearTimeout(delayDebounceFn)
  //     } else {
  //       handleSearch()
  //     }
  //   }
  // }, [searchTerm, selectedFilters])

  const handleReset = () => {
    setSearchTerm("")
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      status: 2,
      orderBy: 1,
      keyword: "",
    }
    dispatch(getAllIssueType(payload))
    navigate(`/support-issues?page=${1}`)
    setSelectedFilters({
      status: 2,
      sortOrder: 1,
    })
  }

  const handleStatusUpdate = (id, status) => {
    setSelectedId(id)
    const payload = {
      id,
      status: status === 1 ? "0" : "1",
    }
    dispatch(updateIssueTypeStatus(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const handleDelete = () => {
    const payload = {
      id: itemIdToDelete,
      status: "2", // 2 FOR DELETE
    }
    dispatch(deleteIssueType(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowDeleteModal(false)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={t("Support Issues")}
                isAddBtn={usersPermissions.create ? true : false}
                addBtnText={t("Add New")}
                onAddBtnClick={() => navigate("/support-issues/add-issue")}
              />
              <CardBody>
                <Filters
                  filters={filters}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  searchBy={t("Title")}
                />
                <div className="">
                  {loading ? (
                    <TableShimmer row={7} col={4} />
                  ) : issueTypes.length === 0 ? (
                    <p className="text-center my-2">
                      {t("No support issues found")}
                    </p>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light full_th">
                        <tr>
                          <th>{t("S. No.")}</th>
                          <th className="min_80">{t("Title")}</th>
                          {/* <th>Display Order</th> */}
                          {/* <th>Attachment</th> */}
                          <th>{t("Status")}</th>
                          <th>{t("Action")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {issueTypes?.map(
                          (
                            {
                              _id,
                              displayOrder,
                              isAttachmentRequired,
                              status,
                              title,
                              titleAr,
                            },
                            index
                          ) => (
                            <tr
                              key={index}
                              onClick={() =>
                                navigate(
                                  `/support-issues/issue-details?id=${_id}`
                                )
                              }
                              className="cursor-pointer"
                            >
                              <th scope="row">
                                {" "}
                                {(currentPage - 1) * 10 + index + 1}
                              </th>
                              <td>{language === "sa" ? titleAr : title}</td>
                              {/* <td>{displayOrder}</td> */}
                              {/* <td>{isAttachmentRequired ? "Yes" : "No"}</td> */}
                              <td>
                                {status === 1 ? (
                                  <Badge className="bg-success">
                                    {t("Active")}
                                  </Badge>
                                ) : (
                                  <Badge className="bg-danger">
                                    {t("Deactive")}
                                  </Badge>
                                )}
                              </td>
                              <td className="d-flex align-items-center gap-2">
                                <div className="gap-1 d-flex">
                                  {usersPermissions.edit && (
                                    <Switch
                                      onClickHandler={e => {
                                        e.stopPropagation()
                                        handleStatusUpdate(_id, status)
                                      }}
                                      isChecked={status === 1}
                                      disabled={_id === selectedId && isLoading}
                                    />
                                  )}
                                  <UncontrolledDropdown>
                                    <DropdownToggle
                                      tag="a"
                                      className="card-drop"
                                      onClick={e => e.stopPropagation()}
                                    >
                                      <i className="mdi mdi-dots-vertical font-size-18"></i>
                                    </DropdownToggle>
                                    <DropdownMenu className="dropdown-menu-end">
                                      {usersPermissions.edit && (
                                        <DropdownItem
                                          onClick={e => {
                                            e.stopPropagation()
                                            navigate(
                                              `/support-issues/edit-issue?id=${_id}`
                                            )
                                          }}
                                        >
                                          <i className="mdi mdi-pencil font-size-16 text-success me-1"></i>
                                          {t("Edit")}
                                        </DropdownItem>
                                      )}

                                      <DropdownItem
                                        onClick={e => {
                                          e.stopPropagation()
                                          navigate(
                                            `/support-issues/issue-details?id=${_id}`
                                          )
                                        }}
                                      >
                                        <i className="mdi mdi-eye font-size-16 text-primary me-1"></i>
                                        {t("View")}
                                      </DropdownItem>

                                      {usersPermissions.delete && (
                                        <DropdownItem
                                          onClick={e => {
                                            e.stopPropagation()
                                            setItemIdToDelete(_id)
                                            setShowDeleteModal(true)
                                          }}
                                        >
                                          <i className="mdi mdi-trash-can font-size-16 text-danger me-1"></i>
                                          {t("Delete")}
                                        </DropdownItem>
                                      )}
                                    </DropdownMenu>
                                  </UncontrolledDropdown>
                                </div>
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </Table>
                  )}
                </div>
                {!loading && (
                  <div className="mt-3">
                    <Paginations
                      data={issueTypes}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
      <DeleteModal
        show={showDeleteModal}
        isLoading={isLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setShowDeleteModal(false)}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this {{name}}?", {
          name: t("Support Issue"),
        })}
      />
    </div>
  )
}

export default IssueLists
